import { motion } from "framer-motion";
import { useI18n } from "@/lib/i18n";
import { TID } from "@/lib/testIds";
import SeoHead from "@/components/SeoHead";
import { Mail, Phone, MapPin, Clock, ArrowUpRight } from "lucide-react";

const BG =
  "https://images.pexels.com/photos/273209/pexels-photo-273209.jpeg?auto=compress&cs=tinysrgb&w=2400";

export default function Contact() {
  const { t } = useI18n();
  const checklist = t("contact_page.checklist");

  return (
    <div data-testid={TID.pageContact}>
      <SeoHead titleKey="seo.contact_title" descKey="seo.contact_desc" />

      <section className="relative bg-[color:var(--gc-navy)] text-white pt-36 pb-24 md:pt-44 md:pb-28 overflow-hidden">
        <div className="absolute inset-0 opacity-45">
          <img src={BG} alt="" className="w-full h-full object-cover gc-kenburns" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-[color:var(--gc-navy)]/55 via-[color:var(--gc-navy)]/70 to-[color:var(--gc-navy)]" />
        <div className="absolute inset-0 bg-gradient-to-r from-[color:var(--gc-navy)]/85 via-[color:var(--gc-navy)]/45 to-transparent" />
        <div className="absolute inset-0 gc-grid-bg-dark opacity-50" />

        <div className="relative max-w-[1440px] mx-auto px-6 lg:px-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl"
          >
            <p className="gc-eyebrow text-white/60">{t("contact_page.eyebrow")}</p>
            <h1 className="mt-6 font-display text-5xl md:text-7xl lg:text-[88px] leading-[0.96] tracking-tight font-[700]">
              {t("contact_page.title")}
            </h1>
            <p className="mt-6 max-w-2xl text-lg md:text-xl text-white/80 leading-relaxed">
              {t("contact_page.lead")}
            </p>
          </motion.div>
        </div>
      </section>

      <section className="bg-[color:var(--gc-navy)] text-white pb-24 md:pb-32">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10 border border-white/10">
            {/* Email */}
            <motion.a
              data-testid={TID.contactEmail}
              href={`mailto:${t("common.email")}`}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="group bg-[color:var(--gc-navy)] hover:bg-[color:var(--gc-ink)] transition-colors p-8 md:p-12 lg:p-14 flex flex-col gap-6"
            >
              <div className="flex items-center justify-between">
                <span className="gc-eyebrow text-white/50">{t("contact_page.email_label")}</span>
                <Mail className="w-5 h-5 text-white/60 group-hover:text-white" />
              </div>
              <div className="font-display text-3xl md:text-4xl lg:text-5xl font-[700] tracking-tight break-all">
                {t("common.email")}
              </div>
              <div className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.16em] text-white/70 group-hover:text-white">
                <span className="w-6 h-px bg-[color:var(--gc-red)]" />
                {t("contact_page.email_label")}
                <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
              </div>
            </motion.a>

            {/* Phone */}
            <motion.a
              data-testid={TID.contactPhone}
              href={t("common.phone_href")}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.08 }}
              className="group bg-[color:var(--gc-navy)] hover:bg-[color:var(--gc-ink)] transition-colors p-8 md:p-12 lg:p-14 flex flex-col gap-6"
            >
              <div className="flex items-center justify-between">
                <span className="gc-eyebrow text-white/50">{t("contact_page.phone_label")}</span>
                <Phone className="w-5 h-5 text-white/60 group-hover:text-white" />
              </div>
              <div className="font-display text-3xl md:text-4xl lg:text-5xl font-[700] tracking-tight">
                {t("common.phone")}
              </div>
              <div className="text-sm text-white/65 font-mono tracking-wider">
                {t("contact_page.hours_value")}
              </div>
            </motion.a>

            {/* Address */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.12 }}
              className="bg-[color:var(--gc-navy)] p-8 md:p-12 lg:p-14 flex flex-col gap-6"
            >
              <div className="flex items-center justify-between">
                <span className="gc-eyebrow text-white/50">{t("contact_page.address_label")}</span>
                <MapPin className="w-5 h-5 text-white/60" />
              </div>
              <div className="font-display text-2xl md:text-3xl font-[500] tracking-tight text-white/85">
                {t("contact_page.address_value")}
              </div>
            </motion.div>

            {/* Hours */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.16 }}
              className="bg-[color:var(--gc-navy)] p-8 md:p-12 lg:p-14 flex flex-col gap-6"
            >
              <div className="flex items-center justify-between">
                <span className="gc-eyebrow text-white/50">{t("contact_page.hours_label")}</span>
                <Clock className="w-5 h-5 text-white/60" />
              </div>
              <div className="font-display text-2xl md:text-3xl font-[500] tracking-tight text-white/85">
                {t("contact_page.hours_value")}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Checklist */}
      <section className="bg-white py-24 md:py-32">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <p className="gc-eyebrow">{t("contact_page.eyebrow")}</p>
              <h2 className="mt-5 font-display text-3xl md:text-4xl lg:text-5xl font-[700] tracking-tight text-[color:var(--gc-ink)] leading-tight">
                {t("contact_page.checklist_title")}
              </h2>
            </div>
            <div className="lg:col-span-7">
              <ul className="border-t border-[color:var(--gc-line)]">
                {Array.isArray(checklist) &&
                  checklist.map((c, i) => (
                    <li
                      key={i}
                      className="flex items-start gap-5 border-b border-[color:var(--gc-line)] py-5"
                    >
                      <span className="font-mono text-xs tracking-[0.16em] text-[color:var(--gc-mist)] pt-1 w-10">
                        /{String(i + 1).padStart(2, "0")}
                      </span>
                      <span className="text-[15px] md:text-base text-[color:var(--gc-ink)] leading-relaxed">
                        {c}
                      </span>
                    </li>
                  ))}
              </ul>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
